import { DEFAULT_UNCERTAINTY_SPREAD_CAP } from './confidence-score'
import { roundCurrency } from './money'
import type { RefundStatement } from './refund-statement'
import type { AirlineDeductions, DollarUncertainty, DollarUncertaintyCode } from './types'

/**
 * Uncertainty in dollars, not points (SPEC.md §10.8). Each doubt the deductions raise with a known
 * dollar effect (YQ/YR the carrier may refund, a fee Sabre may have under-quoted…) gives the lowest
 * and highest refund it could mean. The doubts don't matter when the whole range sits inside the
 * accuracy tolerance, or when no reading in it changes what happens to the case: whether the
 * customer gets money back or owes it, whether anything comes back from the supplier, and whether
 * it comes back as cash. Otherwise the quote needs a fact-check, and the agent sees why.
 */

/** The accuracy measure's tolerance: max($25, 2% of the refund). */
const TOLERANCE_FLOOR = 25
const TOLERANCE_SHARE = 0.02

export type UncertaintyOutcome = 'customer-owes' | 'nothing-from-supplier' | 'travel-credit'

export interface UncertaintyIssue {
  code: DollarUncertaintyCode
  /** Total across tickets, booking currency. */
  amount: number
  direction: 'lower' | 'higher'
  /** For the agent, e.g. "YQ/YR AUD 42.00 uncertain — the carrier isn't in the YQ/YR table". */
  label: string
}

export interface RefundUncertainty {
  /** One per code, largest first. */
  issues: UncertaintyIssue[]
  /** Lowest and highest supplier refund across the readings (never below 0). */
  supplierRefund: { low: number; high: number }
  refundToPax: { low: number; high: number }
  tolerance: number
  withinTolerance: boolean
  /** What differs between the two ends of the range. Empty: every reading leads to the same case. */
  outcomeChanges: UncertaintyOutcome[]
  /** high − low of the supplier refund, before it stops at 0. */
  spread: number
  /** The widest range a quote can stay high with on the same outcome (the scoring settings). */
  spreadCap: number
  /** No issues, or none that matter (inside the tolerance, or the same outcome within the spread cap). */
  settled: boolean
}

export interface AssessUncertaintyInput {
  statement: RefundStatement
  deductions: AirlineDeductions
  currency: string
  /** Defaults to DEFAULT_UNCERTAINTY_SPREAD_CAP. */
  spreadCap?: number
}

export function assessRefundUncertainty({
  statement,
  deductions,
  currency,
  spreadCap = DEFAULT_UNCERTAINTY_SPREAD_CAP,
}: AssessUncertaintyInput): RefundUncertainty {
  const issues = groupIssues(deductions.uncertainties ?? [], currency)
  const lower = roundCurrency(issues.filter(i => i.direction === 'lower').reduce((sum, i) => sum + i.amount, 0))
  const higher = roundCurrency(issues.filter(i => i.direction === 'higher').reduce((sum, i) => sum + i.amount, 0))
  const tolerance = roundCurrency(Math.max(TOLERANCE_FLOOR, TOLERANCE_SHARE * statement.expectedSupplierRefund))

  // A doubt in the deductions moves both ledgers by the same amount.
  const supplierLow = roundCurrency(statement.supplierLedger.raw - lower)
  const supplierHigh = roundCurrency(statement.supplierLedger.raw + higher)
  const paxLow = roundCurrency(statement.paxLedger.raw - lower)
  const paxHigh = roundCurrency(statement.paxLedger.raw + higher)
  const totalPaid = statement.paxLedger.totalPaid

  const outcomeChanges: UncertaintyOutcome[] = []
  if (paxLow < 0 !== paxHigh < 0) outcomeChanges.push('customer-owes')
  if (supplierLow <= 0 !== supplierHigh <= 0) outcomeChanges.push('nothing-from-supplier')
  if (issues.some(i => i.code === 'travel-credit-likely') && statement.expectedSupplierRefund > 0) {
    outcomeChanges.push('travel-credit')
  }
  const withinTolerance = lower <= tolerance && higher <= tolerance
  const spread = roundCurrency(lower + higher)

  return {
    issues,
    supplierRefund: { low: Math.max(0, supplierLow), high: Math.max(0, supplierHigh) },
    refundToPax: {
      low: Math.min(Math.max(0, paxLow), totalPaid),
      high: Math.min(Math.max(0, paxHigh), totalPaid),
    },
    tolerance,
    withinTolerance,
    outcomeChanges,
    spread,
    spreadCap,
    settled: issues.length === 0 || withinTolerance || (outcomeChanges.length === 0 && spread <= spreadCap),
  }
}

function groupIssues(uncertainties: DollarUncertainty[], currency: string): UncertaintyIssue[] {
  const byCode = new Map<DollarUncertaintyCode, DollarUncertainty[]>()
  for (const u of uncertainties) byCode.set(u.code, [...(byCode.get(u.code) ?? []), u])
  return [...byCode.entries()]
    .map(([code, items]) => {
      const amount = roundCurrency(items.reduce((sum, u) => sum + u.amount, 0))
      return {
        code,
        amount,
        direction: items[0]!.direction,
        label: label(code, items, `${currency} ${amount.toFixed(2)}`),
      }
    })
    .sort((a, b) => b.amount - a.amount)
}

function label(code: DollarUncertaintyCode, items: DollarUncertainty[], money: string): string {
  switch (code) {
    case 'yqyr-unconfirmed':
      return `YQ/YR ${money} uncertain — the carrier isn't in the YQ/YR table, so it's assumed withheld`
    case 'conditional-tax-code': {
      const taxCodes = [...new Set(items.map(u => u.taxCode))].join('/')
      return `${taxCodes} ${money} uncertain — the fare withholds it only in a situation the booking can't confirm`
    }
    case 'penalty-below-filed-rule':
      return `Cancellation fee ${money} uncertain — Sabre's fee is below the filed fare rule's`
    case 'unverifiable-penalty-clause':
      return `Cancellation fee ${money} uncertain — the rule may cut it on data we don't have`
    case 'travel-credit-likely':
      return `${money} may come back as a travel credit, not cash`
  }
}
